import axios, { AxiosInstance } from 'axios';
import { API_URL, API_ENDPOINTS, API_PREFIX, API_ORIGIN } from '../config/constants.ts';

// Define user type
export interface User {
  id: string;
  name: string;
  email: string;
  role: 'admin' | 'cleaner';
  avatar?: string;
  position?: number;
}

// Create axios instance with default config
const apiClient: AxiosInstance = axios.create({
  baseURL: API_ORIGIN,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token and normalize URL paths
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token && config.headers) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    // Prefix API path for path-style URLs (avoid axios dropping base path)
    if (typeof config.url === 'string') {
      const isAbsolute = /^https?:\/\//i.test(config.url);
      if (!isAbsolute && config.url.startsWith('/') && API_PREFIX && !config.url.startsWith(API_PREFIX)) {
        config.url = `${API_PREFIX}${config.url}`;
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle common errors
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle 401 Unauthorized errors, but skip for login endpoint
    if (error.response && error.response.status === 401) {
      // Skip interceptor handling for login requests - let the login function handle it
      const requestUrl = error.config?.url || '';
      const isLoginRequest = requestUrl.includes('/auth/Access') || requestUrl.includes('/auth/access');
      
      if (!isLoginRequest) {
        // Clear token and redirect to login for other 401 errors
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);


// Report API

// Authentication API
export const authAPI = {
  login: async (loginId: string, password: string): Promise<{ user: User; token: string }> => {
    try {
      const response = await apiClient.post(API_ENDPOINTS.auth.login, { loginId, password });
      
      if (response.data && response.data.token) {
        // Store token
        localStorage.setItem('auth_token', response.data.token);
        
        // Parse JWT to get user info
        const payload = JSON.parse(atob(response.data.token.split('.')[1]));
        
        // Extract email claim - adjust based on your JWT claims
        const email = payload[Object.keys(payload).find(key => key.includes('email')) || ''] || loginId;
        
        // Determine role based on email or JWT claims
        const role: 'admin' | 'cleaner' = email.includes('admin') ? 'admin' : 'cleaner';
        
        // Extract Position from API response or token payload if present
        const position: number | undefined = response.data.Position ?? response.data.position ?? payload.Position ?? payload.position;
        
        // Create user object
        const user: User = {
          id: payload.sub || '1',
          name: payload[Object.keys(payload).find(key => key.includes('name')) || ''] || loginId,
          email: email,
          role: role,
          avatar: `https://ui-avatars.com/api/?name=${email.split('@')[0]}&background=dd4b39&color=fff`,
          position
        };
        
        localStorage.setItem('user', JSON.stringify(user));
        
        return {
          user,
          token: response.data.token
        };
      }
      
      throw new Error('Authentication failed');
    } catch (error: any) {
      console.error('Login error:', error);
      
      // Handle 401 Unauthorized response specifically
      if (error.response && error.response.status === 401) {
        throw new Error('Invalid username or password');
      }
      
      // Handle network errors or other errors
      if (error.message && error.message.includes('Invalid username or password')) {
        throw error;
      }
      
      // For other errors, provide a generic message
      throw new Error(error.response?.data?.message || error.message || 'Invalid username or password');
    }
  },
  
  logout: () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
  }
};
export const reportAPI = {
  getSummary: async (): Promise<any> => {
    const response = await apiClient.get(API_ENDPOINTS.report.getSummary, {
    });
    return response.data.data;
  },
};

// Management API
export const managementAPI = {
  getManagerListing: async (params: { StartDate: string; EndDate: string }) => {
    try {
      console.log('[API] 🕒 Fetching manager listing with params:', params);
      const response = await apiClient.post(API_ENDPOINTS.management.getManagerListing, params);
      console.log('[API] ✅ Successfully fetched manager listing');
      return response.data; // Expecting response.data to contain .data array
    } catch (error) {
      console.error('Error fetching manager listing:', error);
      throw error;
    }
  },
  getHistory: async (params: { StartDate: string; EndDate: string; WorkFlowStatusId?: number; ManagerId: string; PageNumber?: number; PageSize?: number }) => {
    try {
      console.log('[API] 🕒 Fetching manager history with params:', params);
      const response = await apiClient.post(API_ENDPOINTS.management.getHistory, params);
      console.log('[API] ✅ Successfully fetched manager history');
      return response.data;
    } catch (error) {
      console.error('Error fetching manager history:', error);
      throw error;
    }
  }
};

// Assign/Pending Jobs API helpers
export const pendingJobAPI = {
  getPendingJobs: async (params: { StartDate: string; EndDate: string; PageNumber?: number; PageSize?: number; SearchText?: string }) => {
    try {
      console.log('[API] 🕒 Fetching pending jobs with params:', params);
      const response = await apiClient.post(API_ENDPOINTS.management.getPendingJob, params);
      console.log('[API] ✅ Successfully fetched pending jobs');
      return response.data;
    } catch (error) {
      console.error('Error fetching pending jobs:', error);
      throw error;
    }
  }
};

export const managerDirectoryAPI = {
  getManagers: async () => {
    try {
      console.log('[API] 🕒 Fetching managers...');
      const response = await apiClient.get(API_ENDPOINTS.management.getManager);
      console.log('[API] ✅ Successfully fetched managers');
      return response.data;
    } catch (error) {
      console.error('Error fetching managers:', error);
      throw error;
    }
  }
};

export const assignmentAPI = {
  updateScheduleManager: async (payload: { ManagerId: string[]; ScheduleId: number }) => {
    try {
      console.log('[API] 🕒 Updating schedule manager:', payload);
      const response = await apiClient.post(API_ENDPOINTS.management.updateScheduleManager, payload);
      return response; // caller can check status === 200
    } catch (error) {
      console.error('Error updating schedule manager:', error);
      throw error;
    }
  }
};

// Lookup API for reference data
export const lookupAPI = {
  // Generic function to fetch any lookup type by ID
  getLookupByTypeId: async (lookupTypeId: number) => {
    try {
      console.log(`[API] 🕒 Fetching lookup data for type ID: ${lookupTypeId}...`);
      const response = await apiClient.get(API_ENDPOINTS.lookup.getLookupById(lookupTypeId));
      console.log(`[API] ✅ Successfully fetched lookup data for type ID: ${lookupTypeId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching lookup data for type ID ${lookupTypeId}:`, error);
      throw error;
    }
  },

  // HR: Leave types master list (id, name) from lookup service
  getLeaveTypes: async (): Promise<Array<{ id: number; name: string }>> => {
    try {
      const response = await apiClient.get(API_ENDPOINTS.lookup.getLeaveTypes);
      return response.data?.data || [];
    } catch (error) {
      console.error('Error fetching leave types lookup:', error);
      throw error;
    }
  },

  // Document types
  getDocumentTypes: async (): Promise<Array<{ id: number; name: string }>> => {
    try {
      const response = await apiClient.get(API_ENDPOINTS.lookup.getDocumentTypes);
      return response.data?.data || [];
    } catch (error) {
      console.error('Error fetching document types:', error);
      throw error;
    }
  },

  // Transaction types
  getTransactionTypes: async (): Promise<Array<{ id: number; name: string }>> => {
    try {
      const response = await apiClient.get(API_ENDPOINTS.lookup.getTransactionTypes);
      return response.data?.data || [];
    } catch (error) {
      console.error('Error fetching transaction types:', error);
      throw error;
    }
  },

  // Worker types (Company)
  getWorkerTypes: async (): Promise<Array<{ id: number; name: string }>> => {
    try {
      const response = await apiClient.get(API_ENDPOINTS.lookup.getWorkerTypes);
      return response.data?.data || [];
    } catch (error) {
      console.error('Error fetching worker types:', error);
      throw error;
    }
  },

  // Collection methods
  getCollectionMethods: async (): Promise<Array<{ id: number; name: string }>> => {
    try {
      const response = await apiClient.get(API_ENDPOINTS.lookup.getCollectionMethods);
      return response.data?.data || [];
    } catch (error) {
      console.error('Error fetching collection methods:', error);
      throw error;
    }
  },

  getRecommendations: async () => {
    try {
      const response = await apiClient.get(API_ENDPOINTS.lookup.getRecommendations);
      return response.data.recommendations;
    } catch (error) {
      console.error('Error fetching recommendations:', error);
      throw error;
    }
  },
  
  getPestTypes: async () => {
    try {
      const response = await apiClient.get(API_ENDPOINTS.lookup.getPestTypes);
      return response.data;
    } catch (error) {
      console.error('Error fetching pest types:', error);
      throw error;
    }
  },
  
  getClassTypes: async () => {
    try {
      console.log('[API] 🕒 Fetching pesticide class types...');
      return await lookupAPI.getLookupByTypeId(16);
    } catch (error) {
      console.error('Error fetching pesticide class types:', error);
      throw error;
    }
  },
  
  getPesticideUsedSelection: async () => {
    try {
      console.log('[API] 🕒 Fetching pesticide used options...');
      return await lookupAPI.getLookupByTypeId(13);
    } catch (error) {
      console.error('Error fetching pesticide used options:', error);
      throw error;
    }
  },
  
  getActiveIngredients: async () => {
    try {
      console.log('[API] 🕒 Fetching active ingredients...');
      return await lookupAPI.getLookupByTypeId(14);
    } catch (error) {
      console.error('Error fetching active ingredients:', error);
      throw error;
    }
  },
  
  getApplicationMethods: async () => {
    try {
      console.log('[API] 🕒 Fetching application methods...');
      return await lookupAPI.getLookupByTypeId(15);
    } catch (error) {
      console.error('Error fetching application methods:', error);
      throw error;
    }
  },
  
  getInfestationTypes: async () => {
    try {
      console.log('[API] 🕒 Fetching infestation types...');
      const response = await apiClient.get('/lookup/InfestationType');
      console.log('[API] ✅ Successfully fetched infestation types');
      return response.data;
    } catch (error) {
      console.error('Error fetching infestation types:', error);
      throw error;
    }
  }
};

// Schedule API
export const scheduleAPI = {
  getSchedules: async (params = { PageNumber: 1, PageSize: 10 }) => {
    try {
      console.log('[API] 🕒 Fetching schedules with params:', params);
      const response = await apiClient.get(API_ENDPOINTS.schedule.base, { params });
      console.log('[API] ✅ Successfully fetched schedules');
      return response;
    } catch (error) {
      console.error('Error fetching schedules:', error);
      throw error;
    }
  },
  
  getScheduleById: async (id: number) => {
    try {
      const response = await apiClient.get(API_ENDPOINTS.schedule.getOrder(id));
      return response.data;
    } catch (error) {
      console.error(`Error fetching schedule ${id}:`, error);
      throw error;
    }
  },
  
  getUpcomingServices: async (filter = {}) => {
    try {
      const response = await apiClient.post(API_ENDPOINTS.schedule.getUpcomingServices, filter);
      return response.data;
    } catch (error) {
      console.error('Error fetching upcoming services:', error);
      throw error;
    }
  },
  
  getHistory: async (filter = {}) => {
    try {
      console.log('[API] 🕒 Fetching history with params:', filter);
      const response = await apiClient.post('/schedule/GetHistory', filter);
      console.log('[API] ✅ Successfully fetched history');
      return response.data;
    } catch (error) {
      console.error('Error fetching history:', error);
      throw error;
    }
  },
  
  getScheduleHistory: async (id: string | number, params = { PageNumber: 1, PageSize: 10 }) => {
    try {
      console.log(`[API] 🕒 Fetching history for schedule ${id} with params:`, params);
      const response = await apiClient.post(`/schedule/GetCustomerHistory/${id}`, params);
      console.log(`[API] ✅ Successfully fetched history for schedule ${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching history for schedule ${id}:`, error);
      throw error;
    }
  },

  // Calendar: get event days within a date range
  getEventDays: async (payload: { StartDate: string; EndDate: string }) => {
    try {
      const response = await apiClient.post('/Schedule/GetEventDays', payload);
      return response.data || [];
    } catch (error) {
      console.error('Error fetching event days:', error);
      throw error;
    }
  },
  
  saveNotes: async (id: string | number, data: { Remarks: string }) => {
    try {
      const response = await apiClient.post(`/schedule/SaveNotes/${id}`, data);
      return response.data;
    } catch (error) {
      console.error('Error saving notes:', error);
      throw error;
    }
  },
  
  markAsCompleted: async (id: string | number, data: {
    OtherRemarks: string;
    InfestationLevel: number;
    PostTreatmentActivity: number;
    SiteManagerCompletedBy: string;
    AffectedArea: string;
    Recommendation: Array<{
      Id: number;
      AdditionalInformation: string;
    }>;
    // Ensure TreatedPests is an array of objects
    TreatedPests: Array<{
      Id: number;
      AdditionalInformation: string;
    }>;
    // Update to use array structure for PestInfestationLevels
    PestInfestationLevels: Array<{
      Id: number;
      Level: string;
      Area: string;
      AdditionalInformation?: string;
    }>;
    OtherPestSpecification?: string;
    IsRequiredServiceReport?: boolean;
    ContactNo?: string;
  }) => {
    try {
      const response = await apiClient.post(`/schedule/MarkAsCompleted/${id}`, data);
      return response.data;
    } catch (error) {
      console.error('Error marking schedule as completed:', error);
      throw error;
    }
  },
  
  updateCustomerFeedback: async (formData: FormData) => {
    try {
      const response = await apiClient.post('/schedule/UpdateCustomerFeedback', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error updating customer feedback:', error);
      throw error;
    }
  },
  
  // Update manager working date/time
  updateManagerWorkingDateTime: async (
    id: string | number,
    payload: { StartWorkingTime: string | null; EndWorkingTime: string | null }
  ) => {
    try {
      const response = await apiClient.post(API_ENDPOINTS.schedule.updateManagerWorkingDateTime(id), payload);
      return response.data;
    } catch (error) {
      console.error(`Error updating manager working times for schedule ${id}:`, error);
      throw error;
    }
  },

  uploadImage: async (id: string | number, files: File[], typeId: number) => {
    try {
      const formData = new FormData();
      files.forEach(file => {
        formData.append('UploadFile', file);
      });
      formData.append('TypeId', typeId.toString());
      
      const response = await apiClient.post(`/schedule/uploadimage/${id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error uploading images:', error);
      throw error;
    }
  },
  
  getScheduleDetails: async (id: string | number) => {
    try {
      console.log(`[API] 🔍 Fetching schedule details for ID: ${id}`);
      const response = await apiClient.get(`/Schedule/GetSchedule/${id}`);
      console.log(`[API] ✅ Schedule details fetched successfully for ID: ${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching schedule ${id}:`, error);
      throw error;
    }
  },
  
  getImages: async (id: string | number, typeId: number) => {
    try {
      console.log(`[API] 📸 Fetching images for schedule ID: ${id}, typeId: ${typeId}`);
      const response = await apiClient.post(`/schedule/GetImages/${id}`, { 
        typeId
      });
      console.log(`[API] ✅ Images fetched successfully for ID: ${id}, typeId: ${typeId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching images for schedule ${id}:`, error);
      throw error;
    }
  },
  
  getPesticides: async (scheduleId: string | number) => {
    try {   
      console.log(`[API] 🧪 Fetching pesticides for schedule ID: ${scheduleId}`);
      const response = await apiClient.get(`/schedule/GetPesticides/${scheduleId}`);
      console.log(`[API] ✅ Pesticides fetched successfully for schedule ID: ${scheduleId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching pesticides for schedule ${scheduleId}:`, error);
      throw error;
    }
  },
  
  savePesticides: async (scheduleId: string | number, data: any) => {
    try {
      console.log(`[API] 💾 Saving pesticides for schedule ID: ${scheduleId}`);
      const response = await apiClient.post(`/schedule/SavePesticides/${scheduleId}`, data);
      console.log(`[API] ✅ Pesticides saved successfully for schedule ID: ${scheduleId}`);
      return response.data;
    } catch (error) {
      console.error(`Error saving pesticides for schedule ${scheduleId}:`, error);
      throw error;
    }
  },
  
  updatePesticides: async (scheduleId: string | number, data: Array<{
    Id: number;
    PesticideId: number;
    IngredientId: number;
    ClassId: number;
    QuantityOfFinal: string;
    ApplicationId: number;
    IsDirty: boolean;
  }>) => {
    try {
      console.log(`[API] 🔄 Updating pesticides for schedule ID: ${scheduleId}`);
      const response = await apiClient.post(`/schedule/UpdatePesticides/${scheduleId}`, data);
      console.log(`[API] ✅ Pesticides updated successfully for schedule ID: ${scheduleId}`);
      return response.data;
    } catch (error) {
      console.error(`Error updating pesticides for schedule ${scheduleId}:`, error);
      throw error;
    }
  },
  
  deleteImage: async (imageId: number) => {
    try {
      console.log(`[API] 🗑️ Deleting image with ID: ${imageId}`);
      const response = await apiClient.post(`/schedule/DeleteImage/${imageId}`);
      console.log(`[API] ✅ Image deleted successfully, ID: ${imageId}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting image with ID ${imageId}:`, error);
      throw error;
    }
  },
  
  checkIn: async (scheduleId: number, checkFile: File | null, lat: string, lng: string) => {
    try {
      console.log(`[API] 🕒 Checking in ${scheduleId === 0 ? 'directly' : `for schedule ID: ${scheduleId}`}`);
      
      // Validate required parameters
      if (!checkFile) {
        throw new Error('Image file is required for check-in');
      }
      
      // Create FormData directly
      const formData = new FormData();
      formData.append('CheckFile', checkFile);
      formData.append('Lat', lat);
      formData.append('Nlng', lng);
      
      // Log for debugging
      console.log('Preparing to upload file:', {
        fileName: checkFile.name,
        fileType: checkFile.type,
        fileSize: `${Math.round(checkFile.size / 1024)} KB`,
        lat, lng
      });
      
      // Get auth token for the request
      const token = localStorage.getItem('auth_token');

      // If scheduleId is 0, use the direct check-in endpoint without an ID
      const url = scheduleId === 0 
        ? `${API_URL}/schedule/CheckIn` 
        : `${API_URL}/schedule/CheckIn/${scheduleId}`;
      
      // Use fetch API directly to ensure proper FormData submission
      const response = await fetch(url, {
        method: 'POST',
        body: formData, // FormData will automatically set the correct content type
        headers: {
          // Include auth token but do NOT set Content-Type for FormData
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        }
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`Check-in API error: ${response.status}`, errorText);
        throw new Error(errorText || `Server returned ${response.status}`);
      }
      
      // Backend returns 200 OK but may not include a JSON body
      // Check response content type before trying to parse JSON
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        // Only try to parse JSON if the response contains JSON
        const data = await response.json();
        console.log(`[API] ✅ Check-in successful ${scheduleId === 0 ? 'direct check-in' : `for schedule ID: ${scheduleId}`}`, data);
        return data;
      }
      
      // If no JSON content but response is OK, just return success
      console.log(`[API] ✅ Check-in successful ${scheduleId === 0 ? 'direct check-in' : `for schedule ID: ${scheduleId}`} (no data returned)`);
      return { success: true };
    } catch (error) {
      console.error(`Error checking in ${scheduleId === 0 ? 'directly' : `for schedule ${scheduleId}`}:`, error);
      throw error;
    }
  },

  // Get working schedules (those with workFlowStatusId === 1)
  getWorkingSchedules: async () => {
    try {
      console.log('[API] 🕒 Fetching working schedules...');
      const response = await apiClient.get(`${API_URL}/schedule/GetWorkingSchedules`);
      console.log('[API] ✅ Successfully fetched working schedules');
      return response;
    } catch (error) {
      console.error('Error fetching working schedules:', error);
      throw error;
    }
  },
  
  // Get check-in history with pagination
  getCheckInHistory: async (pageNumber: number, pageSize: number) => {
    try {
      console.log(`[API] 🕒 Fetching check-in history (page ${pageNumber}, size ${pageSize})...`);
      
      // Add timeout to prevent endless API calls
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
      
      const response = await apiClient.post(`/schedule/GetCheckIn`, {
        PageNumber: pageNumber,
        PageSize: pageSize
      }, {
        signal: controller.signal
      });
      
      clearTimeout(timeoutId); // Clear timeout if successful
      
      // Validate response structure before returning
      if (response && response.data) {
        console.log('[API] ✅ Successfully fetched check-in history');
        return response.data; // Return the full response object, including the data property
      } else {
        throw new Error('Invalid response structure from API');
      }
    } catch (error: any) {
      // Check if request was aborted due to timeout
      if (error.name === 'AbortError') {
        console.error('Check-in history request timed out');
        throw new Error('Request timed out. Please try again later.');
      }
      
      // Log the actual error for debugging
      console.error('Error fetching check-in history:', error);
      
      // Add a delay to prevent rapid retries
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      throw error;
    }
  },
  
  printServiceReport: async (scheduleId: string | number) => {
    try {
      console.log(`[API] 🖨️ Printing service report for schedule ID: ${scheduleId}`);
      const response = await apiClient.get(`/Schedule/PrintServiceReport/${scheduleId}`, {
        responseType: 'blob' // Get response as blob for file download
      });
      console.log(`[API] ✅ Service report printed successfully for schedule ID: ${scheduleId}`);
      return response;
    } catch (error) {
      console.error(`Error printing service report for schedule ${scheduleId}:`, error);
      throw error;
    }
  },

  // HR: Get leave types and balances for current user/year
  getLeaveTypes: async () => {
    try {
      const response = await apiClient.post('/profile/GetLeaveType');
      // Expected shape: { data: [{ LeaveYear, Balance, LeaveCount, LeaveType }] }
      return response.data?.data || [];
    } catch (error) {
      console.error('Error fetching leave types:', error);
      throw error;
    }
  },

  // HR: Get today's check-in log for current user
  getTodayCheckInLog: async (timeNow: string) => {
    try {
      // Backend expects a DateTime parameter named timeNow in the JSON body
      const response = await apiClient.post('/profile/GetCheckInLog', { timeNow });
      return response.data?.data || null;
    } catch (error) {
      console.error('Error fetching today check-in log:', error);
      throw error;
    }
  },

  // HR: Clock-out current active check-in (assumes server infers the record by user/date)
  clockOut: async () => {
    try {
      const response = await apiClient.post('/profile/ClockOut');
      return response.data || { success: true };
    } catch (error) {
      console.error('Error clocking out:', error);
      throw error;
    }
  },

  // HR: Clock-out by record id
  clockOutById: async (id: number) => {
    try {
      const response = await apiClient.post(`/Schedule/CheckOut/${id}`);
      return response.data || { success: true };
    } catch (error) {
      console.error('Error clocking out by id:', error);
      throw error;
    }
  },

  // HR: Get recommended/standard working time for the current user
  getStandardWorkingTime: async () => {
    try {
      const response = await apiClient.post('/profile/GetStandardWorkingTime');
      return response.data?.data || null;
    } catch (error) {
      console.error('Error fetching standard working time:', error);
      throw error;
    }
  },

  // HR: Get leave application history grouped by upcoming/past
  getLeaveHistory: async () => {
    try {
      const response = await apiClient.post('/profile/GetLeaveHistory');
      // Response shape: { upcoming: [...], Past: [...] }
      return response.data || { upcoming: [], Past: [] };
    } catch (error) {
      console.error('Error fetching leave history:', error);
      throw error;
    }
  },
  
  // Get dashboard statistics
  getDashboardStats: async () => {
    try {
      console.log('[API] 📊 Fetching dashboard statistics...');
      const response = await apiClient.get('/schedule/GetDashboard');
      console.log('[API] ✅ Successfully fetched dashboard statistics');
      return response.data.data; // API returns { data: { checkIn, completed, monthCount } }
    } catch (error) {
      console.error('Error fetching dashboard statistics:', error);
      throw error;
    }
  },
  
  // New method to get pending signature customers
  getPendingSignatureCustomers: async (params = { PageNumber: 1, PageSize: 10 }) => {
    try {
      console.log('[API] 🕒 Fetching pending signature customers with params:', params);
      const response = await apiClient.post('/schedule/GetPendingSignatureCustomer', params);
      console.log('[API] ✅ Successfully fetched pending signature customers');

      // Structure the response to include both data and metadata
      return {
        data: response.data.data || [],
        totalRecords: response.data.totalRecords ,
        currentPage: response.data.currentPage || params.PageNumber,
        pageTotal: response.data.pageTotal
      };
    } catch (error) {
      console.error('Error fetching pending signature customers:', error);
      throw error;
    }
  },
  
  // Function to cancel a schedule
  cancelSchedule: async (scheduleId: string | number) => {
    try {
      console.log(`[API] 🚫 Cancelling schedule ID: ${scheduleId}`);
      const response = await apiClient.post(`/schedule/Cancel/${scheduleId}`);
      console.log(`[API] ✅ Successfully cancelled schedule ID: ${scheduleId}`);
      return response.data;
    } catch (error) {
      console.error(`Error cancelling schedule ${scheduleId}:`, error);
      // Don't transform the error here, let the component handle it
      throw error;
    }
  },
  
  // New method to send signature reminder
  sendSignatureReminder: async (scheduleId: string | number) => {
    try {
      console.log(`[API] 📧 Sending signature reminder for schedule ID: ${scheduleId}`);
      const response = await apiClient.post(`/schedule/SendSignatureReminder/${scheduleId}`);
      console.log(`[API] ✅ Successfully sent signature reminder for schedule ID: ${scheduleId}`);
      return response.data;
    } catch (error) {
      console.error(`Error sending signature reminder for schedule ${scheduleId}:`, error);
      throw error;
    }
  }
};

// Profile API (HR-related endpoints)
const buildLeaveFormData = (payload: {
    LeaveTypeId: number;
    LeaveStartDate: string;
    LeaveEndDate?: string;
    HalfDay: boolean;
    Remarks?: string;
    FileItem?: File | null;
    LeaveSessionId?: number;
  }) => {
    const formData = new FormData();
    formData.append('LeaveTypeId', String(payload.LeaveTypeId));
    formData.append('LeaveStartDate', payload.LeaveStartDate);
    if (payload.LeaveEndDate) formData.append('LeaveEndDate', payload.LeaveEndDate);
    formData.append('HalfDay', String(payload.HalfDay));
    if (payload.Remarks) formData.append('Remarks', payload.Remarks);
    if (payload.FileItem) formData.append('FileItem', payload.FileItem);
    if (payload.LeaveSessionId !== undefined) {
      formData.append('LeaveSessionId', String(payload.LeaveSessionId));
    }
    return formData;
  };

export const profileAPI = {

  // Get documents by type
  getDocuments: async (documentTypeId: number) => {
    try {
      const response = await apiClient.get(API_ENDPOINTS.profile.getDocuments(documentTypeId));
      return response.data?.data || [];
    } catch (error) {
      console.error('Error fetching documents:', error);
      throw error;
    }
  },

  // Download a specific document by type and filename
  downloadDocument: async (documentTypeId: number, fileName: string, password: string): Promise<Blob> => {
    try {
      const url = `${API_URL}${API_ENDPOINTS.profile.getDownloadDocument(documentTypeId)}`;
      const token = localStorage.getItem('auth_token');
      const resp = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ fileName, password }),
      });
      if (!resp.ok) {
        const text = await resp.text();
        const msg = text?.replace(/^"|"$/g, '').trim() || `HTTP ${resp.status}`;
        throw new Error(msg || `HTTP ${resp.status}`);
      }
      return await resp.blob();
    } catch (error) {
      console.error('Error downloading document:', error);
      throw error;
    }
  },

  downloadLeaveDocument: async (id: number | string, fileName: string): Promise<Blob> => {
    try {
      const url = `${API_URL}${API_ENDPOINTS.profile.downloadLeaveDocument(id, fileName)}`;
      const token = localStorage.getItem('auth_token');
      const resp = await fetch(url, {
        headers: {
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        }
      });
      if (!resp.ok) {
        throw new Error(`HTTP ${resp.status}`);
      }
      return await resp.blob();
    } catch (error) {
      console.error('Error downloading leave document:', error);
      throw error;
    }
  },

  deleteLeaveApplication: async (id: number | string) => {
    try {
      
      const response = await apiClient.post(API_ENDPOINTS.profile.deleteLeave(id));
      return response.data;
    } catch (error) {
      console.error('Error deleting leave application:', error);
      throw error;
    }
  },

  // Submit leave application with optional file attachment
  leaveApplication: async (payload: {
    LeaveTypeId: number;
    LeaveStartDate: string; // YYYY-MM-DD
    LeaveEndDate?: string;  // YYYY-MM-DD (optional when HalfDay)
    HalfDay: boolean;
    Remarks?: string;
    FileItem?: File | null;
    LeaveSessionId?: number; // 1 = AM, 2 = PM, 0/undefined for full day
  }) => {
    try {
      const formData = buildLeaveFormData(payload);
      const token = localStorage.getItem('auth_token');

      const response = await fetch(`${API_URL}${API_ENDPOINTS.profile.leaveApplication}`, {
        method: 'POST',
        body: formData,
        headers: {
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        }
      });
      if (!response.ok) {
        const ct = response.headers.get('content-type') || '';
        let message = `HTTP ${response.status}`;
        if (ct.includes('application/json')) {
          const data = await response.json();
          if (Array.isArray(data)) {
            message = data.join(', ');
          } else {
            message = data?.title || data?.detail || data?.message || JSON.stringify(data);
          }
        } else {
          message = await response.text();
        }
        throw new Error(message || `HTTP ${response.status}`);
      }
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        return await response.json();
      }
      return { success: true };
    } catch (error) {
      console.error('Error submitting leave application:', error);
      throw error;
    }
  },

  updateLeaveApplication: async (payload: {
    Id: number;
    LeaveTypeId: number;
    LeaveStartDate: string;
    LeaveEndDate?: string;
    HalfDay: boolean;
    Remarks?: string;
    FileItem?: File | null;
    LeaveSessionId?: number;
  }) => {
    try {
      const { Id, ...rest } = payload;
      const formData = buildLeaveFormData(rest);
      formData.append('Id', String(Id));

      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}${API_ENDPOINTS.profile.updateLeaveApplication}`, {
        method: 'POST',
        body: formData,
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        }
      });
      if (!response.ok) {
        const ct = response.headers.get('content-type') || '';
        let message = `HTTP ${response.status}`;
        if (ct.includes('application/json')) {
          const data = await response.json();
          if (Array.isArray(data)) {
            message = data.join(', ');
          } else {
            message = data?.title || data?.detail || data?.message || JSON.stringify(data);
          }
        } else {
          message = await response.text();
        }
        throw new Error(message || `HTTP ${response.status}`);
      }
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        return await response.json();
      }
      return { success: true };
    } catch (error) {
      console.error('Error updating leave application:', error);
      throw error;
    }
  },

  // Get feed items with pagination
  getFeed: async (params: { PageNumber: number; PageSize: number }) => {
    try {
      console.log('[API] 🕒 Fetching feed with params:', params);
      const response = await apiClient.post(API_ENDPOINTS.profile.getFeed, params);
      console.log('[API] ✅ Successfully fetched feed');
      return response.data;
    } catch (error: any) {
      console.error('Error fetching feed:', error);
      // Preserve the error response status for proper error handling
      if (error.response) {
        const enhancedError = new Error(error.response.data?.message || error.message || 'Failed to fetch feed');
        (enhancedError as any).response = error.response;
        (enhancedError as any).status = error.response.status;
        throw enhancedError;
      }
      throw error;
    }
  },

  // Get tasks with pagination
  getTasks: async (params: { PageNumber: number; PageSize: number; StatusId?: number }) => {
    try {
      console.log('[API] 🕒 Fetching tasks with params:', params);
      const response = await apiClient.post(API_ENDPOINTS.profile.getTasks, params);
      console.log('[API] ✅ Successfully fetched tasks');
      return response.data;
    } catch (error) {
      console.error('Error fetching tasks:', error);
      throw error;
    }
  },

  // Get task summary (pending count, total points, etc.)
  getTaskSummary: async () => {
    try {
      console.log('[API] 🕒 Fetching task summary...');
      const response = await apiClient.get(`${API_ENDPOINTS.profile.getTasks}/summary`);
      console.log('[API] ✅ Successfully fetched task summary');
      return response.data;
    } catch (error) {
      console.error('Error fetching task summary:', error);
      throw error;
    }
  },

  // Mark task as complete
  markTaskComplete: async (taskId: string | number) => {
    try {
      console.log(`[API] ✅ Marking task ${taskId} as complete...`);
      const response = await apiClient.post(API_ENDPOINTS.profile.markTaskComplete(taskId));
      console.log(`[API] ✅ Successfully marked task ${taskId} as complete`);
      return response.data;
    } catch (error) {
      console.error(`Error marking task ${taskId} as complete:`, error);
      throw error;
    }
  },

  // Get timeline file (PDF download)
  getTimeLineFile: async (id: string | number, fileName: string): Promise<Blob> => {
    try {
      console.log(`[API] 📄 Fetching timeline file for ID: ${id}, fileName: ${fileName}`);
      const url = `${API_URL}${API_ENDPOINTS.profile.getTimeLineFile(id, fileName)}`;
      const token = localStorage.getItem('auth_token');
      const response = await fetch(url, {
        headers: {
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        }
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      return await response.blob();
    } catch (error) {
      console.error(`Error fetching timeline file for ID ${id}:`, error);
      throw error;
    }
  },

  // Transaction Request API
  submitTransactionRequest: async (formData: FormData) => {
    try {
      console.log('[API] 🕒 Submitting transaction request...');
      const response = await apiClient.post(API_ENDPOINTS.profile.transactionRequest, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        }
      });
      console.log('[API] ✅ Successfully submitted transaction request');
      return response.data;
    } catch (error) {
      console.error('Error submitting transaction request:', error);
      throw error;
    }
  },

  updateTransactionRequest: async (id: number, formData: FormData) => {
    try {
      console.log(`[API] 🕒 Updating transaction request ID: ${id}...`);
      const response = await apiClient.post(API_ENDPOINTS.profile.updateTransactionRequest(id), formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        }
      });
      console.log('[API] ✅ Successfully updated transaction request');
      return response.data;
    } catch (error) {
      console.error('Error updating transaction request:', error);
      throw error;
    }
  },

  updateSignature: async (id: number, signatureFile: File) => {
    try {
      console.log(`[API] 🕒 Updating signature for transaction ID: ${id}...`);
      const formData = new FormData();
      formData.append('SignatureFile', signatureFile);
      
      const response = await apiClient.post(API_ENDPOINTS.profile.updateSignature(id), formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        }
      });
      console.log('[API] ✅ Successfully updated signature');
      return response.data;
    } catch (error) {
      console.error('Error updating signature:', error);
      throw error;
    }
  },

  getTransactionRequests: async (params: { PageNumber: number; PageSize: number }) => {
    try {
      console.log('[API] 🕒 Fetching transaction requests with params:', params);
      const response = await apiClient.post(API_ENDPOINTS.profile.getTransactionRequests, params);
      console.log('[API] ✅ Successfully fetched transaction requests');
      return response.data;
    } catch (error) {
      console.error('Error fetching transaction requests:', error);
      throw error;
    }
  },

  deleteTransactionRequest: async (id: number | string) => {
    try {
      console.log(`[API] 🗑️ Deleting transaction request ID: ${id}...`);
      const response = await apiClient.post(API_ENDPOINTS.profile.deleteTransactionRequest(id));
      console.log(`[API] ✅ Successfully deleted transaction request ID: ${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting transaction request ${id}:`, error);
      throw error;
    }
  }
};

// Claim API
export const claimAPI = {
  extractReceiptText: async (receiptFile: File): Promise<{ text: string; parsed?: { amount?: number; date?: string; merchant?: string; description?: string } }> => {
    try {
      console.log('[API] 🕒 Extracting text from receipt...');
      const token = localStorage.getItem('auth_token');
      
      const formData = new FormData();
      formData.append('receiptFile', receiptFile);
      
      const response = await fetch(`${API_URL}${API_ENDPOINTS.claim.extractReceiptText}`, {
        method: 'POST',
        body: formData,
        headers: {
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        }
      });
      
      if (!response.ok) {
        const contentType = response.headers.get('content-type') || '';
        let message = `HTTP ${response.status}`;
        if (contentType.includes('application/json')) {
          const data = await response.json();
          if (Array.isArray(data)) {
            message = data.join(', ');
          } else {
            message = data?.title || data?.detail || data?.message || JSON.stringify(data);
          }
        } else {
          message = await response.text();
        }
        throw new Error(message || `HTTP ${response.status}`);
      }
      
      const result = await response.json();
      console.log('[API] ✅ Successfully extracted text from receipt');
      return {
        text: result.text || '',
        parsed: result.parsed ? {
          amount: result.parsed.amount,
          date: result.parsed.date,
          merchant: result.parsed.merchant,
          description: result.parsed.description
        } : undefined
      };
    } catch (error) {
      console.error('Error extracting text from receipt:', error);
      throw error;
    }
  },

  createClaim: async (formData: FormData) => {
    try {
      console.log('[API] 🕒 Creating claim...');
      const token = localStorage.getItem('auth_token');
      
      const response = await fetch(`${API_URL}${API_ENDPOINTS.claim.createClaim}`, {
        method: 'POST',
        body: formData,
        headers: {
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        }
      });
      
      if (!response.ok) {
        const contentType = response.headers.get('content-type') || '';
        let message = `HTTP ${response.status}`;
        if (contentType.includes('application/json')) {
          const data = await response.json();
          if (Array.isArray(data)) {
            message = data.join(', ');
          } else {
            message = data?.title || data?.detail || data?.message || JSON.stringify(data);
          }
        } else {
          message = await response.text();
        }
        throw new Error(message || `HTTP ${response.status}`);
      }
      
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        const result = await response.json();
        console.log('[API] ✅ Successfully created claim');
        return result;
      }
      return { success: true };
    } catch (error) {
      console.error('Error creating claim:', error);
      throw error;
    }
  },

  getClaims: async (params: { PageNumber?: number; PageSize?: number; StatusId?: number }) => {
    try {
      console.log('[API] 🕒 Fetching claims with params:', params);
      const response = await apiClient.post(API_ENDPOINTS.claim.getClaims, {
        PageNumber: params.PageNumber || 1,
        PageSize: params.PageSize || 10,
        StatusId: params.StatusId // 1 = pending/rejected, 2 = approved, undefined = all
      });
      console.log('[API] ✅ Successfully fetched claims');
      return response.data;
    } catch (error) {
      console.error('Error fetching claims:', error);
      throw error;
    }
  },

  getClaimById: async (id: number | string) => {
    try {
      console.log(`[API] 🕒 Fetching claim ID: ${id}...`);
      const response = await apiClient.get(API_ENDPOINTS.claim.getClaim(id));
      console.log(`[API] ✅ Successfully fetched claim ID: ${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching claim ${id}:`, error);
      throw error;
    }
  },

  getMyFranchisees: async () => {
    try {
      const response = await apiClient.get(API_ENDPOINTS.claim.getMyFranchisees);
      return response.data?.data || [];
    } catch (error) {
      console.error('Error fetching user franchisees:', error);
      throw error;
    }
  }
};

// Payment Verification API
export const paymentVerificationAPI = {
  extractReceiptData: async (receiptFile: File): Promise<{ text: string; parsed?: { amount?: number; receiptNo?: string; paymentDate?: string; paymentMethod?: string } }> => {
    try {
      console.log('[API] 🕒 Extracting payment receipt data...');
      const token = localStorage.getItem('auth_token');
      
      const formData = new FormData();
      formData.append('receiptFile', receiptFile);
      
      const response = await fetch(`${API_URL}${API_ENDPOINTS.paymentVerification.extractReceiptData}`, {
        method: 'POST',
        body: formData,
        headers: {
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        }
      });
      
      if (!response.ok) {
        const contentType = response.headers.get('content-type') || '';
        let message = `HTTP ${response.status}`;
        if (contentType.includes('application/json')) {
          const data = await response.json();
          if (Array.isArray(data)) {
            message = data.join(', ');
          } else {
            message = data?.title || data?.detail || data?.message || JSON.stringify(data);
          }
        } else {
          message = await response.text();
        }
        throw new Error(message || `HTTP ${response.status}`);
      }
      
      const result = await response.json();
      console.log('[API] ✅ Successfully extracted receipt data');
      return result;
    } catch (error: any) {
      console.error('Error extracting receipt data:', error);
      throw new Error(error.message || 'Failed to extract receipt data');
    }
  },

  searchServiceOrders: async (query: string): Promise<{ data: Array<{ id: number; serviceOrderNo: string; invoiceNo: string; serviceOrderDate: string; customerName: string; customerContactNo: string; customerAddress: string; totalAmount: number; paidAmount: number; remainingBalance: number }> }> => {
    try {
      console.log(`[API] 🕒 Searching service orders: ${query}...`);
      const response = await apiClient.get(API_ENDPOINTS.paymentVerification.searchServiceOrders, {
        params: { query }
      });
      console.log('[API] ✅ Successfully searched service orders');
      return response.data;
    } catch (error) {
      console.error('Error searching service orders:', error);
      throw error;
    }
  },

  submitPayment: async (formData: FormData): Promise<{ id: number; message: string }> => {
    try {
      console.log('[API] 🕒 Submitting payment verification...');
      const token = localStorage.getItem('auth_token');
      
      const response = await fetch(`${API_URL}${API_ENDPOINTS.paymentVerification.submitPayment}`, {
        method: 'POST',
        body: formData,
        headers: {
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        }
      });
      
      if (!response.ok) {
        const contentType = response.headers.get('content-type') || '';
        let message = `HTTP ${response.status}`;
        if (contentType.includes('application/json')) {
          const data = await response.json();
          if (Array.isArray(data)) {
            message = data.join(', ');
          } else {
            message = data?.title || data?.detail || data?.message || JSON.stringify(data);
          }
        } else {
          message = await response.text();
        }
        throw new Error(message || `HTTP ${response.status}`);
      }
      
      const result = await response.json();
      console.log('[API] ✅ Successfully submitted payment verification');
      return result;
    } catch (error: any) {
      console.error('Error submitting payment verification:', error);
      throw new Error(error.message || 'Failed to submit payment verification');
    }
  },

  getPaymentHistory: async (pageNumber: number = 1, pageSize: number = 20): Promise<{ data: Array<any>; totalRecords: number; currentPage: number; pageTotal: number }> => {
    try {
      console.log(`[API] 🕒 Fetching payment history (page ${pageNumber})...`);
      const response = await apiClient.get(API_ENDPOINTS.paymentVerification.getPaymentHistory, {
        params: { pageNumber, pageSize }
      });
      console.log('[API] ✅ Successfully fetched payment history');
      return response.data;
    } catch (error) {
      console.error('Error fetching payment history:', error);
      throw error;
    }
  }
};

export default apiClient; 